import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Operation, OperationRequest } from '../models/operation-profile.model';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Toggle } from '../../../shared/components/forms/toggle';

export type DialogMode = 'add' | 'edit';

/** Weekday form controls paired with their short labels, for the toggle row. */
const WEEKDAYS: { control: string; label: string }[] = [
  { control: 'monday', label: 'Mon' },
  { control: 'tuesday', label: 'Tue' },
  { control: 'wednesday', label: 'Wed' },
  { control: 'thursday', label: 'Thu' },
  { control: 'friday', label: 'Fri' },
  { control: 'saturday', label: 'Sat' },
  { control: 'sunday', label: 'Sun' },
];

@Component({
  selector: 'app-operation-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    Modal,
    ModalFooterDirective,
    FormField,
    TextInput,
    NumberInput,
    Toggle,
  ],
  templateUrl: './operation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationDialogComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);

  @Input() operation: Operation | null = null;
  @Input() mode: DialogMode = 'add';
  @Input() profileId!: number;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<OperationRequest>();

  /** Drives the modal shell's two-way `open`; closing routes through onClose(). */
  readonly open = signal(true);

  readonly weekdays = WEEKDAYS;

  form = this.fb.group({
    operationId: ['', Validators.required],
    sleep: [false],
    shutdown: [false],
    renderInterval: [3600000, [Validators.required, Validators.min(0)]],
    connectionInterval: [86400000, [Validators.required, Validators.min(0)]],
    illumination: [1.0, [Validators.required, Validators.min(0), Validators.max(1)]],
    startDate: [''],
    endDate: [''],
    startTime: [''],
    endTime: [''],
    batteryPowerStatusList: [''],
    // Days checkboxes
    monday: [false],
    tuesday: [false],
    wednesday: [false],
    thursday: [false],
    friday: [false],
    saturday: [false],
    sunday: [false],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operation'] || changes['mode']) {
      if (this.operation && this.mode === 'edit') {
        const days = this.parseDays(this.operation.days);
        this.form.patchValue({
          operationId: this.operation.operationId,
          sleep: this.operation.sleep,
          shutdown: this.operation.shutdown,
          renderInterval: this.operation.renderInterval,
          connectionInterval: this.operation.connectionInterval,
          illumination: this.operation.illumination,
          startDate: this.operation.startDate || '',
          endDate: this.operation.endDate || '',
          startTime: this.operation.startTime || '',
          endTime: this.operation.endTime || '',
          batteryPowerStatusList: this.formatStatusList(this.operation.batteryPowerStatusList),
          ...days,
        });
        this.form.get('operationId')?.disable();
      } else {
        this.form.reset({
          operationId: '',
          sleep: false,
          shutdown: false,
          renderInterval: 3600000,
          connectionInterval: 86400000,
          illumination: 1.0,
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          batteryPowerStatusList: '',
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        });
        this.form.get('operationId')?.enable();
      }
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      const days = this.buildDaysArray(formValue);

      this.save.emit({
        operationId: formValue.operationId!,
        sleep: formValue.sleep!,
        shutdown: formValue.shutdown!,
        renderInterval: formValue.renderInterval!,
        connectionInterval: formValue.connectionInterval!,
        illumination: formValue.illumination!,
        startDate: formValue.startDate || null,
        endDate: formValue.endDate || null,
        startTime: formValue.startTime || null,
        endTime: formValue.endTime || null,
        days: days,
        batteryPowerStatusList: this.parseStatusList(formValue.batteryPowerStatusList),
      });
    }
  }

  private parseDays(days: string | string[] | null): Record<string, boolean> {
    const result: Record<string, boolean> = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };

    if (!days) return result;

    // Handle both string and array formats
    const daysArray = Array.isArray(days)
      ? days.map(d => d.toUpperCase())
      : days.split(',').map(d => d.trim().toUpperCase());

    if (daysArray.includes('MONDAY')) result['monday'] = true;
    if (daysArray.includes('TUESDAY')) result['tuesday'] = true;
    if (daysArray.includes('WEDNESDAY')) result['wednesday'] = true;
    if (daysArray.includes('THURSDAY')) result['thursday'] = true;
    if (daysArray.includes('FRIDAY')) result['friday'] = true;
    if (daysArray.includes('SATURDAY')) result['saturday'] = true;
    if (daysArray.includes('SUNDAY')) result['sunday'] = true;

    return result;
  }

  private buildDaysArray(formValue: any): string[] {
    const days: string[] = [];
    if (formValue.monday) days.push('MONDAY');
    if (formValue.tuesday) days.push('TUESDAY');
    if (formValue.wednesday) days.push('WEDNESDAY');
    if (formValue.thursday) days.push('THURSDAY');
    if (formValue.friday) days.push('FRIDAY');
    if (formValue.saturday) days.push('SATURDAY');
    if (formValue.sunday) days.push('SUNDAY');
    return days;
  }

  // Convert array to comma-separated string for form input
  private formatStatusList(statusList: string[] | string | null): string {
    if (!statusList) return '';
    if (Array.isArray(statusList)) return statusList.join(', ');
    return statusList;
  }

  // Convert comma-separated string to array for API
  private parseStatusList(statusList: string | null): string[] {
    if (!statusList || !statusList.trim()) return [];
    return statusList.split(',').map(s => s.trim()).filter(s => s.length > 0);
  }
}
